import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { Text, Button, makeStyles, shorthands } from '@fluentui/react-components';
import { PersonAccountsRegular, SignOutRegular } from '@fluentui/react-icons';
import LoadingPage from './Components/LoadingPage';
import { useSignInState } from './Components/signInState';
import { loginRequest } from './Components/authConfig';
import authErrorHandling from './Components/authErrorHandling';
import backgroundImage from './Assets/backgrounds/basecamphero.png';
import logo from './Assets/Logos/logo-white.svg';
import { useHandleLogout } from './Components/HandleLogout';
import useFetch from './Hooks/useFetchAPI';
import { useConfig } from './context/ConfigContext';
import RandomQuote from './Components/RandomQuote';

const useStyles = makeStyles({
    pageWrapper: {
        boxSizing: 'border-box',
        padding: '8px',
        backgroundColor: '#ECECEC',
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        position: 'relative', // Allows absolutely positioned children (shade, logo)
    },
    containerLight: {
        minHeight: '300px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        ...shorthands.gap('1rem'),
        ...shorthands.padding('2rem'),
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        borderRadius: '12px',
    },
    containerDark: {
        minHeight: '300px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        ...shorthands.gap('1rem'),
        ...shorthands.padding('2rem'),
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        borderRadius: '12px',
    },
    welcomeText: {
        fontSize: '2rem',
        textAlign: 'center',
    },
    instructionText: {
        fontSize: '1.2rem',
        textAlign: 'center',
    },
    buttons: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    logoWrapper: {
        position: 'absolute',
        top: '30px',
        left: '30px',
        zIndex: 3, // Above the shade and card
        width: '150px', // Adjust as needed
        height: 'auto',
    },
    logoImage: {
        width: 'auto', // adjust as needed
        height: '60px',
    },
});

const VerifyCustomer: React.FC = () => {
    const styles = useStyles();
    const location = useLocation();
    const { appName } = useConfig();
    const { instance, accounts, inProgress } = useMsal();
    const { handleLogout } = useHandleLogout();
    const [tenantId, setTenantId] = useState<string | null>('');
    const [customerEmail, setCustomerEmail] = useState<string | null>('');
    const { signInState } = useSignInState();
    const [authError, setauthError] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [currentTheme, setCurrentTheme] = useState('lightmodecontainer');

    const { fetchAPI: verifyCustomer, apiData: verifyCustomerData, isLoading: isLoading, serverError: serverError } = useFetch({ scope: 'AdminUser' });
    const [isVerifiedLocal, setIsVerifiedLocal] = useState(false);
    
    useEffect(() => {
        setCurrentTheme(signInState?.userInfo?.[0]?.ThemePreference === 'dark' ? 'Dark' : 'Light');
    }, [signInState]);

    useEffect(() => {
        const query = new URLSearchParams(location.search);
        const customerID = query.get('customer');
        const custEmail = query.get('email');
        setTenantId(customerID);
        setCustomerEmail(custEmail);

        if (!customerID || !custEmail) {
            setErrorMessage("Sorry, please use the provided link from your verification email.");
        }
    }, [location]);

    const verifyCustomerAsync = async () => {
        await verifyCustomer({ url: 'customers/verify', method: 'POST', body: { customer: tenantId, email: customerEmail } });
    };

    useEffect(() => {
        if (inProgress === 'none' &&
            accounts.length > 0 &&
            signInState.isAuthed &&
            signInState.userInfo?.[0]?.Email === customerEmail &&
            signInState.customerInfo?.[0]?.TenantID === tenantId &&
            !isVerifiedLocal) {
            verifyCustomerAsync();
            console.log('running verification function');
        }
    }, [inProgress, accounts, signInState, customerEmail, tenantId, isVerifiedLocal]);

    // Once the verification API call returns successfully, update local state to reflect verification completion
    useEffect(() => {
        if (verifyCustomerData && verifyCustomerData.message === 'Customer verified successfully') {
            console.log('verifyCustomerData:', verifyCustomerData);
            setIsVerifiedLocal(true);
        }
    }, [verifyCustomerData]);

    const onLogin = async () => {
        try {
            const loginResponse = await instance.loginPopup(loginRequest);
            console.log('[HandleLogin] Login response:', loginResponse);
        } catch (error) {
            setauthError(authErrorHandling(error));
            console.log('[HandleLogin] Login error:', error);
        }
    };

    return (
        <div className={styles.pageWrapper}>
            <div className={styles.logoWrapper}>
                <img className={styles.logoImage} src={logo} alt="Basecamp Logo" />
            </div>
            <div className={styles['container' + currentTheme as keyof typeof styles]}>
                <Text className={styles.welcomeText}>Welcome to {appName}!</Text>

                <RandomQuote />

                {!tenantId ? (
                    <Text className={styles.instructionText}>
                        Please use the link found in your verification e-mail to finish verification.
                    </Text>
                ) : (
                    <>
                        <p>Tenant ID: {tenantId}</p>
                        {accounts.length < 1 && (
                            <Text className={styles.instructionText}>
                                Please log in to begin the verification process and enable platform features.
                            </Text>
                        )}
                        <div className={styles.buttons}>
                            {accounts.length < 1 ? (
                                <Button appearance='primary' size="medium" icon={<PersonAccountsRegular />} onClick={onLogin}>
                                    Log in
                                </Button>
                            ) : (
                                <Button size="medium" icon={<SignOutRegular />} onClick={handleLogout}>
                                    Logout
                                </Button>
                            )}
                        </div>
                        {inProgress === 'none' && accounts.length > 0 && !isVerifiedLocal ? (
                            signInState.isAuthed &&
                                signInState.userInfo?.[0]?.Email === customerEmail &&
                                signInState.customerInfo?.[0]?.TenantID === tenantId ? (
                                <p>Processing the verification...</p>
                            ) : (
                                <div style={{ textAlign: 'center' }}>
                                    <p>Error validating company.</p>
                                    <p>
                                        Please use the link provided in the verification email and with the account used to
                                        originally register the company.
                                    </p>
                                </div>
                            )
                        ) : (
                            signInState.isAuthed &&
                                signInState.userInfo?.[0]?.Email === customerEmail &&
                                signInState.customerInfo?.[0]?.TenantID === tenantId &&
                                isVerifiedLocal ? (
                                <>
                                    <p>Verification Complete, please continue to the app and enjoy using {appName}!</p>

                                    <div className={styles.buttons}>
                                        <Button size="medium" appearance="primary" onClick={() => { globalThis.location.href = '/'; }} >
                                            Continue to App
                                        </Button>
                                    </div>
                                </>

                            ) : null
                        )}
                    </>
                )}
                {inProgress === 'login' && <LoadingPage message="Logging in..." />}
            </div>
        </div>
    );
};

export default VerifyCustomer;
